import React, { useState, useEffect } from 'react';
import { Calendar, AlertTriangle, Clock, Zap, Settings } from 'lucide-react';
import BatchProcessingTimeline from './components/BatchProcessingTimeline';
import AnomalyDetectionPanel from './components/AnomalyDetectionPanel';
import DiscrepancyResolutionQueue from './components/DiscrepancyResolutionQueue';
import BatchScheduleConfig from './components/BatchScheduleConfig';
import AutomationMetrics from './components/AutomationMetrics';
import ProcessingLogs from './components/ProcessingLogs';
import QuickActionWidget from './components/QuickActionWidget';

const AutomatedSettlementReconciliationCenter = () => {
  const [activeTab, setActiveTab] = useState('timeline');
  const [showScheduleConfig, setShowScheduleConfig] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('idle');
  const [automationMetrics, setAutomationMetrics] = useState({
    batchSuccessRate: 98.5,
    anomaliesDetected: 23,
    autoResolvedDiscrepancies: 156,
    processingEfficiency: 94.2
  });

  const [batchSchedules, setBatchSchedules] = useState([
    {
      id: 1,
      name: 'Daily Settlement Reconciliation',
      schedule: '0 2 * * *',
      timezone: 'UTC',
      status: 'active',
      lastRun: '2026-02-04T02:00:00Z',
      nextRun: '2026-02-05T02:00:00Z',
      duration: '12m 34s',
      successRate: 99.2
    },
    {
      id: 2,
      name: 'Hourly Quick Reconciliation',
      schedule: '0 * * * *',
      timezone: 'UTC',
      status: 'active',
      lastRun: '2026-02-04T03:00:00Z',
      nextRun: '2026-02-04T04:00:00Z',
      duration: '3m 21s',
      successRate: 97.8
    },
    {
      id: 3,
      name: 'Weekly Deep Analysis',
      schedule: '0 0 * * 0',
      timezone: 'UTC',
      status: 'paused',
      lastRun: '2026-01-28T00:00:00Z',
      nextRun: '2026-02-09T00:00:00Z',
      duration: '45m 12s',
      successRate: 98.5
    }
  ]);

  const handleManualTrigger = (scheduleId) => {
    setProcessingStatus('running');
    // Simulate batch processing
    setTimeout(() => {
      setProcessingStatus('completed');
      setTimeout(() => setProcessingStatus('idle'), 2000);
    }, 3000);
  };

  const handleEmergencyStop = () => {
    setProcessingStatus('stopped');
    setTimeout(() => setProcessingStatus('idle'), 1500);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Zap className="w-8 h-8 text-blue-600" />
              Automated Settlement Reconciliation Center
            </h1>
            <p className="text-gray-600 mt-1">
              Scheduled batch processing, anomaly detection, and intelligent discrepancy resolution
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowScheduleConfig(true)}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Configure Rules
            </button>
            <div className="px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${
                processingStatus === 'running' ? 'bg-green-500 animate-pulse' :
                processingStatus === 'completed' ? 'bg-green-500' :
                processingStatus === 'stopped'? 'bg-red-500' : 'bg-gray-400'
              }`} />
              <span className="text-sm font-medium text-gray-700">
                {processingStatus === 'running' ? 'Processing...' :
                 processingStatus === 'completed' ? 'Completed' :
                 processingStatus === 'stopped'? 'Stopped' : 'System Ready'}
              </span>
            </div>
          </div>
        </div>

        {/* Automation Metrics */}
        <AutomationMetrics metrics={automationMetrics} />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Main Control Area - 12 cols on large screens, 8 cols on xl */}
        <div className="lg:col-span-12 xl:col-span-8 space-y-6">
          {/* Tab Navigation */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="border-b border-gray-200">
              <div className="flex gap-1 p-2">
                <button
                  onClick={() => setActiveTab('timeline')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'timeline' ?'bg-blue-50 text-blue-700' :'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Calendar className="w-4 h-4 inline mr-2" />
                  Batch Timeline
                </button>
                <button
                  onClick={() => setActiveTab('anomalies')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'anomalies' ?'bg-blue-50 text-blue-700' :'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  Anomaly Detection
                </button>
                <button
                  onClick={() => setActiveTab('logs')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'logs' ?'bg-blue-50 text-blue-700' :'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Clock className="w-4 h-4 inline mr-2" />
                  Processing Logs
                </button>
              </div>
            </div>

            <div className="p-6">
              {activeTab === 'timeline' && (
                <BatchProcessingTimeline
                  schedules={batchSchedules}
                  onManualTrigger={handleManualTrigger}
                  processingStatus={processingStatus}
                />
              )}
              {activeTab === 'anomalies' && (
                <AnomalyDetectionPanel />
              )}
              {activeTab === 'logs' && (
                <ProcessingLogs />
              )}
            </div>
          </div>
        </div>

        {/* Side Panel - 12 cols on large screens, 4 cols on xl */}
        <div className="lg:col-span-12 xl:col-span-4 space-y-6">
          {/* Quick Action Widgets */}
          <QuickActionWidget
            onManualTrigger={handleManualTrigger}
            onEmergencyStop={handleEmergencyStop}
            processingStatus={processingStatus}
          />

          {/* Discrepancy Resolution Queue */}
          <DiscrepancyResolutionQueue />
        </div>
      </div>

      {/* Batch Schedule Configuration Modal */}
      {showScheduleConfig && (
        <BatchScheduleConfig
          schedules={batchSchedules}
          onClose={() => setShowScheduleConfig(false)}
          onSave={(updatedSchedules) => {
            setBatchSchedules(updatedSchedules);
            setShowScheduleConfig(false);
          }}
        />
      )}
    </div>
  );
};

export default AutomatedSettlementReconciliationCenter;